import {
	DownCircleOutlined,
	ExclamationCircleOutlined,
	FileDoneOutlined,
	FileTextOutlined,
	ReloadOutlined,
	SyncOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, Menu, Popover, Table, Tag, Tooltip } from 'antd';
import { midMonthPayApi } from 'Api/midMonthPays';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { fileSystemService } from 'Api/fileSystem';
import DownloadComponent from 'components/Global/Download';
import Ellipse from 'components/Global/Ellipse';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { CSVSvg, Paid, PaymentUnderProcess, SentToBank } from 'utils/svgs';
import { formatDate, toastText } from 'utils/utils';
import LogsModal from '../LogsModal';
import styles from './index.module.scss';
import MidMonthPayGroupModal from '../payGroupModal';

const { Column } = Table;

type Props = {
	tableData: any;
	totalRecords: number;
	currentPage: number;
	pageSize: number;
	tableChangeHandler: (pagination: any, filter: any, sorter: any) => void;
	isLoading: boolean;
	updateMidMonthStatus: (id: string, status: string) => void;
	fetchMidMonthData: any;
};

const MidMonthPayTable = (props: Props) => {
	const {
		tableData,
		totalRecords,
		currentPage,
		pageSize,
		tableChangeHandler,
		isLoading,
		updateMidMonthStatus,
		fetchMidMonthData,
	} = props;
	const [selectedMidMonthId, setSelectedMidMonthId] = useState<string | null>(
		null
	);
	const [isLogsModalVisible, setIsLogsModalVisible] = useState(false);

	const [isPayGroupModalVisible, setIsPayGroupModalVisible] = useState(false);
	const [midMonthPayGroupData, setMidMonthPayGroupData] = useState<any>([]);
	const [isPayGroupLoading, setIsPayGroupLoading] = useState(false);

	const [filePopoverVisible, setFilePopoverVisible] = useState<{[key: string]: boolean}>({});
	const { data: profileData } = useSelector(
		(state: any) => state?.userProfile
	);
	const handleLogsOpen = (midMonthId: string) => {
		// Commented out API call for now
		// fetchPaymentLogs(midMonthId);

		// Just open the modal
		setSelectedMidMonthId(midMonthId);
		setIsLogsModalVisible(true);
	};

	const handleMenuClick = (record: any, e: any) => {
		if (record.status === e.key) {
			return;
		} else {
			updateMidMonthStatus(record.id, e.key);
		}
	};

		const handleFilePopoverChange = (recordId: string, visible: boolean) => {
		setFilePopoverVisible(prev => ({
			...prev,
			[recordId]: visible
		}));
	};

	const statusMenu = (record: any) => (
		<Menu onClick={(e) => handleMenuClick(record, e)}>
			<Menu.Item key="PAID" className={styles.menuItem}>
				<span className={styles['menuIcon']}>
					<Paid />
				</span>
				Paid
			</Menu.Item>
		</Menu>
	);

	const handleRegenerate = async (midMonthPayId: string) => {
		try {
			await midMonthPayApi.regenerateCsv({
				id: midMonthPayId,
			});
			toastText('Mid month csv has been re generated', 'success');
			fetchMidMonthData();
		} catch (error: any) {
			let message = 'Something went wrong in regenerating payslip';

			if (error?.response?.data?.message) {
				message = error.response.data.message;
			}
			toastText(message, 'error');
		}
	};

	const fetchMidMonthPayGroupData = async (midmonthId: string) => {
		try {
			setIsPayGroupLoading(true);

			const payGroupData = await midMonthPayApi.fetchMidMonthPayGroupData(
				{
					midMonthId: midmonthId,
				}
			);

			setMidMonthPayGroupData(payGroupData.data.data);
		} catch (error: any) {
			toastText(
				error.response?.data?.message ??
					'Something went wrong in fetching mid-month details',
				'error'
			);
		} finally {
			setIsPayGroupLoading(false);
		}
	};
	return (
		<>
			<div>
				<Table
					dataSource={tableData}
					scroll={{ y: 'calc(100vh - 329px)' }}
					pagination={{
						total: totalRecords,
						current: currentPage,
						pageSize: pageSize,
						showSizeChanger: false,
						pageSizeOptions: [10, 20, 50, 100, 200],
					}}
					onChange={tableChangeHandler}
					rowKey={(record) => record.id}
					loading={isLoading}
				>
					<Column
						title="Created Date"
						dataIndex="createdAt"
						key="createdAt"
						className="bg-white"
						sorter={true}
						width={'12%'}
						render={(value) => {
							return formatDate(
								value,
								profileData?.companyDateFormat ?? 'DD/MM/YYYY'
							);
						}}
					/>
					<Column
						title="Details"
						dataIndex="createdAt"
						key="details"
						className="bg-white"
						render={(createdAt, record: any) => {
							const formattedCreatedTime =
								dayjs(createdAt).format('hh:mm A');
							let text = `This system-generated file was created on ${dayjs(
								createdAt
							).format(
								'DD/MM/YYYY'
							)} at ${formattedCreatedTime}.`;

							if (record?.updatedById && record?.updatedAt) {
								const formattedUpdatedTime = dayjs(
									record.updatedAt
								).format('hh:mm A');
								const formattedUpdatedDate = dayjs(
									record.updatedAt
								).format('DD/MM/YYYY');
								text += ` It was last updated on ${formattedUpdatedDate} at ${formattedUpdatedTime}.`;
							}

							return (
								<Ellipse
									maxLength={100}
									message={text}
									isTooltip={true}
									tooltipMessage={text}
									key={record.id}
								/>
							);
						}}
					/>
					<Column
						title="Re Generated By"
						dataIndex="updatedBy"
						key="updatedBy"
						width={'15%'}
						className="bg-white"
						render={(text) => text?.fullName}
					/>
					<Column
						title="Status"
						dataIndex="status"
						key="status"
						width={'20%'}
						className={`${styles['statusColumn']} bg-white`}
						render={(value, record) => {
							let statusText = '';
							let statusClass = '';
							let StatusIcon = null;

							if (value === 'SENT_TO_BANK') {
								statusText = 'Sent to Bank';
								statusClass = styles['status-sent-to-bank'];
								StatusIcon = <SentToBank />;
							} else if (value === 'PAYMENT_UNDER_PROCESS') {
								statusText = 'Payment under Process';
								statusClass =
									styles['status-payment-under-process'];
								StatusIcon = <PaymentUnderProcess />;
							} else if (value === 'FILE_GENERATED') {
								statusText = 'File Generated';
								statusClass = styles['status-file-generated'];
								StatusIcon = <FileDoneOutlined />;
							} else if (value === 'PAID') {
								statusText = 'Paid';
								statusClass = styles['status-paid'];
								StatusIcon = <Paid />;
							} else if (value === 'PROCESSING') {
								statusText = 'Processing';
								statusClass = styles['status-processing'];
								StatusIcon = <SyncOutlined />;
							} else {
								statusText = 'Please select status';
								statusClass = styles['status-placeholder'];
								StatusIcon = <ExclamationCircleOutlined />;
							}

							return (
								<div className={styles['statusContainer']}>
									{value !== 'PAID' && (
										<Dropdown
											overlay={statusMenu(record)}
											trigger={['click']}
										>
											<Button
												type="link"
												onClick={(e) =>
													e.preventDefault()
												}
												className={
													styles['dropdownTrigger']
												}
											>
												<DownCircleOutlined />
											</Button>
										</Dropdown>
									)}
									<Tag
										bordered={false}
										className={`${styles['statusTag']} ${statusClass}`}
									>
										<div className={styles['statusIcon']}>
											{StatusIcon}
											<span>{statusText}</span>
										</div>
									</Tag>
								</div>
							);
						}}
					/>
					<Column
						title="File"
						dataIndex="documentUrl"
						className="bg-white"
						key="documentUrl"
						width={'18%'}
						render={(text, record: any) => {
							// 🔹 Get available files (USD and ZMW) or fallback to primary file
							const availableFiles = record.availableFiles || [
								{
									currency: 'ZMW',
									url: record.attachmentUrl,
									fileName: record.documentName,
								}
							];

							// 🔹 If only one file, show simple download
							if (availableFiles.length === 1) {
								return (
									<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
										<DownloadComponent
											documentUrl={availableFiles[0].url}
											text={availableFiles[0].fileName.split('.')[0]}
											showTooltip={true}
											isLink={true}
											downloadText={
												availableFiles[0].fileName.split('.')[0]
											}
											
											maxLength={20}
										/>
										{record.status !== 'PAID' && (
											<Tooltip title="Regenerate the csv file">
												<Button
													type="text"
													icon={<ReloadOutlined />}
													onClick={() =>
														handleRegenerate(record.id)
													}
													style={{ color: '#584495' }}
												/>
											</Tooltip>
										)}
									</div>
								);
							}

							// 🔹 If multiple files, show popover with file list
							const FilesList = () => {
								return (
									<div>
										{availableFiles.map((file: any) => (
											<div key={file.currency} style={{ marginBottom: '8px' }}>
												<DownloadComponent
													documentUrl={file.url}
													text={file.fileName.split('.')[0]}
													showTooltip={true}
													isLink={true}
													downloadText={file.fileName.split('.')[0]}
													maxLength={50}
												/>
											</div>
										))}
									</div>
								);
							};

							const isVisible = filePopoverVisible[record.id] || false;

							return (
								<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
									<Popover
										content={<FilesList />}
										title=""
										trigger="click"
										placement="bottom"
										open={isVisible}
										onOpenChange={(visible) => handleFilePopoverChange(record.id, visible)}
									>
										<div
											style={{
												display: 'flex',
												alignItems: 'center',
												cursor: 'pointer',
												color: '#584495',
											}}
										>
											<span style={{ margin: '0 8px' }}>
												Mid Month Files ({availableFiles.length})
											</span>
											{isVisible ? <UpOutlined /> : <DownOutlined />}
										</div>
									</Popover>
									{record.status !== 'PAID' && (
										<Tooltip title="Regenerate the csv file">
											<Button
												type="text"
												icon={<ReloadOutlined />}
												onClick={() =>
													handleRegenerate(record.id)
												}
												style={{ color: '#584495' }}
											/>
										</Tooltip>
									)}
								</div>
							);
						}}
					/>

					<Column
						title="View Details"
						dataIndex="initiatePayment"
						key="initiatePayment"
						width={170}
						align="center"
						sorter={false}
						className="bg-white"
						render={(_, record: any) => {
							return (
								<Tooltip
									title={
										'View Pay Group Details for given mid month'
									}
								>
									<Button
										type="link"
										style={{ color: '#584495' }}
										onClick={() => {
											setIsPayGroupModalVisible(true);
											fetchMidMonthPayGroupData(
												record.id
											);
										}}
									>
										View Details
									</Button>
								</Tooltip>
							);
						}}
					/>
					<Column
						title="Logs"
						dataIndex="logs"
						key="logs"
						width={100}
						align="center"
						className="bg-white"
						render={(_, record: any) => (
							<Tooltip
								title={
									record.isLogExists
										? 'View Payment Logs'
										: 'No Payment Logs'
								}
							>
								<Button
									type="text"
									disabled={!record.isLogExists}
									icon={<FileTextOutlined />}
									style={{
										fontSize: '21px',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
									}}
									onClick={() => handleLogsOpen(record.id)}
								/>
							</Tooltip>
						)}
					/>
				</Table>

				{/* Payment Logs Modal */}
				{isLogsModalVisible && selectedMidMonthId && (
					<LogsModal
						isOpen={isLogsModalVisible}
						handleCancel={() => setIsLogsModalVisible(false)}
						midMonthId={selectedMidMonthId}
						updateMidMonthStatus={(
							midMonthId: string,
							status: string
						) => {
							updateMidMonthStatus(midMonthId, status);
						}}
					/>
				)}
			</div>
			{isPayGroupModalVisible && (
				<MidMonthPayGroupModal
					open={isPayGroupModalVisible}
					fetchMidMonthData={fetchMidMonthData}
					isLoading={isPayGroupLoading}
					payGroupData={midMonthPayGroupData}
					closeModal={() => {
						setIsPayGroupModalVisible(false);
					}}
				/>
			)}
		</>
	);
};

export default MidMonthPayTable;
